import React, { useEffect, useState, useCallback } from "react"
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts"
import { GetStats, GetSeries } from "../api"
import { rangeFromFilter, statValue, formatRevenue, useAdsKpiPing } from "./KPITiles"

// KPI metric definitions — colors match the tile palette. `axis` splits the
// chart into a left axis for absolute counts and a right axis for the
// ratio/money metrics so wildly different scales stay readable (Google Ads
// style dual axis).
const METRICS = [
  { key: "impressions", label: "Impr.", color: "#2563eb", axis: "left", fmt: (v) => v },
  { key: "clicks", label: "Clicks", color: "#dc2626", axis: "left", fmt: (v) => v },
  { key: "ctr", label: "CTR", color: "#d97706", axis: "right", fmt: (v) => `${v}%` },
  { key: "signups", label: "Signups", color: "#059669", axis: "left", fmt: (v) => v },
  { key: "revenue", label: "Revenue", color: "#7c3aed", axis: "right", fmt: (v) => `$${v}` },
]
// Start with the two headline metrics drawn; the rest toggle on by click.
const DEFAULT_VISIBLE = new Set(["impressions", "clicks"])

const shortDate = (d) => {
  const [, m, day] = String(d).split("-")
  return `${m}/${day}`
}

/**
 * KPI Dashboard — the stat cards + the trend chart in one component (Google
 * Ads style). Each card shows the total for the range and acts as a toggle:
 * click to show/hide that metric's line in the chart. Active cards are
 * colored; hidden ones are greyed.
 *
 * Props: level ('account'|'campaign'|'ad'), id, account (remote id, optional),
 * range (DateFilterDropdown value).
 */
const KPIDashboard = ({ level, id, account, range }) => {
  const [stats, setStats] = useState(null)
  const [series, setSeries] = useState([])
  const [visible, setVisible] = useState(DEFAULT_VISIBLE)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (!id) return
    let alive = true
    const args = { level, id, account, ...rangeFromFilter(range) }
    ;(async () => {
      const [s, ser] = await Promise.all([
        GetStats(args).catch(() => null),
        GetSeries(args).catch(() => null),
      ])
      if (!alive) return
      setStats(s?.result ? s : null)
      setSeries(ser?.result ? ser.series || [] : [])
    })()
    return () => { alive = false }
  }, [level, id, account, range, tick])

  // Realtime: refetch on ad KPI events (impressions/clicks), debounced.
  useAdsKpiPing(useCallback(() => setTick((t) => t + 1), []))

  const toggle = (key) =>
    setVisible((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })

  const cardValue = (m) => {
    if (!stats) return "—"
    if (m.key === "revenue") {
      return stats.touch === "both"
        ? `${formatRevenue(stats.first?.revenue)} / ${formatRevenue(stats.last?.revenue)}`
        : formatRevenue(stats.revenue)
    }
    if (m.key === "ctr") return `${stats.ctr ?? 0}%`
    if (m.key === "signups") return statValue(stats, "signups")
    return stats[m.key] ?? 0
  }

  const anyRight = METRICS.some((m) => m.axis === "right" && visible.has(m.key))

  return (
    <div className="flex flex-col gap-4">
      {/* cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {METRICS.map((m) => {
          const on = visible.has(m.key)
          return (
            <button
              key={m.key}
              onClick={() => toggle(m.key)}
              className="text-left rounded-lg p-4 border transition-colors"
              style={{
                background: on ? m.color : "transparent",
                borderColor: m.color,
                color: on ? "#fff" : "var(--admin-text, #888)",
              }}
              title={on ? "Hide from chart" : "Show in chart"}
            >
              <div className="text-xs opacity-80 flex items-center gap-1">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ background: on ? "#fff" : m.color }}
                />
                {m.label}
                {stats?.touch === "both" && (m.key === "signups" || m.key === "revenue") ? " (first / last)" : ""}
              </div>
              <div className="text-2xl font-bold mt-1">{cardValue(m)}</div>
            </button>
          )
        })}
      </div>

      {/* chart */}
      <div className="bg-paper border border-admin_bordered rounded-lg p-3" style={{ height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11 }} minTickGap={24} />
            <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={44} allowDecimals={false} />
            {anyRight && <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={44} />}
            <Tooltip
              labelFormatter={shortDate}
              formatter={(value, name) => {
                const m = METRICS.find((x) => x.label === name)
                return [m ? m.fmt(value) : value, name]
              }}
            />
            {METRICS.filter((m) => visible.has(m.key)).map((m) => (
              <Line
                key={m.key}
                yAxisId={m.axis === "right" && anyRight ? "right" : "left"}
                type="monotone"
                dataKey={m.key}
                name={m.label}
                stroke={m.color}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export default KPIDashboard
